import moment from "moment";
import { EVENT_STATUS } from "constant/AppConstants";
import { Appointment } from "types/appointments";
import {
  clientAppointmentSyncing,
  isFailedToSync,
} from "../forms/providerAppointmentForms/appointments.helper";

type StatusFields = Pick<
  Appointment,
  | "crSyncEnabled"
  | "athenaSyncEnabled"
  | "crStatus"
  | "athenaStatus"
  | "scheduledStatus"
  | "parent_type_name"
>;

const CANCEL_SYNCED_STATUS = 103;
const ATHENA_NON_DT_CANCEL_SYNCED_STATUS = 0;

// CR and Athena statuses only count when that sync is enabled for the
// appointment; a disabled downstream is ignored entirely.
export const isClientAppointmentSyncing = (appointment: StatusFields) =>
  (appointment.crSyncEnabled === true &&
    clientAppointmentSyncing(appointment.crStatus)) ||
  (appointment.athenaSyncEnabled === true &&
    clientAppointmentSyncing(appointment.athenaStatus));

export const isClientAppointmentSyncFailed = (appointment: StatusFields) =>
  (appointment.crSyncEnabled === true && isFailedToSync(appointment.crStatus)) ||
  (appointment.athenaSyncEnabled === true &&
    isFailedToSync(appointment.athenaStatus));

// Cancelled in Axon, or the cancel has reached every enabled downstream.
export const isClientAppointmentCancelled = (appointment: StatusFields) => {
  if (appointment.scheduledStatus === EVENT_STATUS.canceled) return true;
  const isCrSyncEnabled = appointment.crSyncEnabled === true;
  const isAthenaSyncEnabled = appointment.athenaSyncEnabled === true;
  if (!isCrSyncEnabled && !isAthenaSyncEnabled) return false;
  const crCancelSuccess =
    !isCrSyncEnabled || appointment.crStatus === CANCEL_SYNCED_STATUS;
  const athenaCancelSuccess =
    !isAthenaSyncEnabled ||
    (appointment.parent_type_name === "DT"
      ? appointment.athenaStatus === CANCEL_SYNCED_STATUS
      : appointment.athenaStatus === ATHENA_NON_DT_CANCEL_SYNCED_STATUS);
  return crCancelSuccess && athenaCancelSuccess;
};

// A visit with no end time is never treated as over (moment(undefined) is "now").
export const isClientAppointmentOver = (
  appointment: Pick<Appointment, "endAt">,
  now: moment.Moment = moment()
) => !!appointment.endAt && moment(appointment.endAt).isSameOrBefore(now);

// Home's "All children" reaches the full-week page as an explicit null
// selectedClientId: keep showing everyone instead of picking the first child.
export const openedWithAllChildren = (state: unknown) =>
  typeof state === "object" &&
  state !== null &&
  "selectedClientId" in state &&
  (state as { selectedClientId: unknown }).selectedClientId === null;

const HTTP_URL = /^https?:\/\/[^\s/]+/i;
// Meeting apps' own links (Zoom, Teams) open the app directly.
const MEETING_APP_URL = /^(zoommtg|zoomus|msteams):\/\/[^\s/]+/i;
// "zoom.us/j/123" style links saved without a scheme: a letter domain and a
// path, so dial-in numbers ("555.123.4567") and names ("Dr.Smith") stay text.
const BARE_HOST_URL = /^([a-z0-9-]+\.)+[a-z]{2,}\/\S*$/i;
// Axon sends this when a visit has no video link saved.
const NO_LINK = "N/A";
const TRAILING_PUNCTUATION = /[.,;)]+$/;

export interface VideoLink {
  // Safe to open: http(s), a meeting app's own link, or a bare host with https://.
  joinUrl?: string;
  // Any other saved text (meeting ID, passcode, dial-in), shown as plain text.
  details?: string;
}

// Splits a saved telehealth link into what can be opened and what is shown as
// text, so nothing saved is lost and nothing unsafe (javascript:, data:) is linked.
export const parseVideoLink = (link?: string | null): VideoLink => {
  const trimmed = link?.trim();
  if (!trimmed || trimmed.toUpperCase() === NO_LINK) return {};
  const [firstWord] = trimmed.split(/\s+/);
  const url = firstWord.replace(TRAILING_PUNCTUATION, "");
  const rest = trimmed.slice(firstWord.length).trim() || undefined;
  if (HTTP_URL.test(url) || MEETING_APP_URL.test(url)) {
    return { joinUrl: url, details: rest };
  }
  if (BARE_HOST_URL.test(url)) return { joinUrl: `https://${url}`, details: rest };
  return { details: trimmed };
};
